import styled from 'styled-components';

import { createUrl, request } from 'app/utils';
import { DashboardListItem, DashboardSelector } from 'features/dashboard-new';
import { useSchool } from 'features/school';
import React, { useCallback, useEffect, useState } from 'react';
import useMenu from './useMenu';
import { Icon } from 'components/icon';
import { useTracking } from 'features/tracking';
import { ConditionalLink, Flex } from 'components';
import { useResources } from 'features/resources';

const Opacity = styled.div`
  flex-grow: 1;
`;

const MenuWrapper = styled.div`
  background: ${({ theme }) => theme.colors.dark_350};
  display: flex;
  flex-direction: column;
  justify-content: stretch;
`;

const MenuScrolling = styled.div`
  flex-grow: 1;
  overflow: auto;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CloseIconWrapper = styled.div`
  display: flex;
  width: 58px;
  align-items: center;
  justify-content: center;
`;

const CloseIcon = styled(Icon)`
  width: 22px;
`;

const CloseLabel = styled.div`
  display: flex;
  font-size: 0.875rem;

  text-transform: uppercase;
  line-height: 1;
  font-weight: 700;
  flex-grow: 1;
`;

const CloseMenu = styled.button<{ schoolAlias: string }>`
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.dark_400};
  border: 0;
  padding: 0;
  height: 54px;
  flex-shrink: 0;
  cursor: pointer;

  ${CloseIcon} {
    color: ${({ theme, schoolAlias }) => (schoolAlias === 'b3' ? theme.colors.white50 : theme.colors.gray_600)};
  }

  ${CloseLabel} {
    color: ${({ theme, schoolAlias }) => (schoolAlias === 'b3' ? theme.colors.white80 : theme.colors.gray_600)};
  }

  &:hover {
    ${CloseIcon} {
      color: ${({ theme, schoolAlias }) => (schoolAlias === 'b3' ? theme.colors.primary_400 : theme.colors.primary_400)};
    }

    ${CloseLabel} {
      color: ${({ theme, schoolAlias }) => (schoolAlias === 'b3' ? theme.colors.primary_400 : theme.colors.white)};
    }
  }
`;

const Wrapper = styled.div`
  display: flex;
  position: fixed;
  flex-direction: row;
  flex-wrap: nowrap;
  width: 100vw;
  height: 100vh;
  background: ${({ theme }) => theme.colors.black80};
  z-index: 10000;

  ${({ theme: { mediaQueries, fontSizes } }) => {
    return `
      ${MenuWrapper} {
        width: 100%;
      }

      ${mediaQueries.desktop} {
        ${MenuWrapper} {
          width: 320px;
        }
      }
    `;
  }}
`;

const MenuIcon = styled(Icon)`
  width: 22px;
  opacity: 0;
`;

const MenuItem = styled(ConditionalLink)`
  display: flex;
  justify-content: space-between;
  padding: 8px 24px;
  color: ${({ theme }) => theme.colors.white80};

  &:hover {
    ${MenuIcon} {
      opacity: 1;
    }

    color: white;
  }
`;

const Menu = () => {
  const [dashboards, setDashboards] = useState<DashboardListItem[]>([]);

  const { isOpened, setMenuOpened } = useMenu();
  const { apiGatewayUrl, schoolAlias } = useSchool();
  const { eventTracker } = useTracking();

  const menuClose = useCallback(() => {
    setMenuOpened(false);
  }, []);

  const escFunction = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      menuClose();
    }
  }, []);

  const fetchDashboards = useCallback(async () => {
    await request<{ dashboards: DashboardListItem[] }>({
      url: '/api/v1/School/Dashboard/List',
      method: 'get',
      baseURL: apiGatewayUrl,
    }).then(({ data }) => {
      setDashboards(data.dashboards);
    });
  }, []);

  useEffect(() => {
    if (isOpened) {
      document.addEventListener('keydown', escFunction, false);
    } else {
      document.removeEventListener('keydown', escFunction, false);
    }
  }, [isOpened]);

  useEffect(() => {
    fetchDashboards();
  }, []);

  const {
    usingTeachers,
    usingReports,
    usingExams,
    showCoursesInMenu,
    schools: { schoolBenndorf, schoolMPC },
  } = useResources();

  let menuProductsLabel = 'Cursos';
  if (schoolBenndorf) {
    menuProductsLabel = 'Produtos';
  } else if (schoolMPC) {
    menuProductsLabel = 'Meu painel';
  }

  const menu = [
    {
      name: 'Home',
      url: createUrl('dashboard'),
      exact: true,
    },
    {
      name: menuProductsLabel,
      url: createUrl('productsList'),
      show: showCoursesInMenu,
    },
    {
      name: 'Professores',
      url: createUrl('teachersList'),
      show: usingTeachers,
    },
    {
      name: 'Relatórios',
      url: createUrl('reportsList'),
      show: usingReports,
    },
    {
      name: 'Avaliações',
      url: createUrl('exams/list'),
      show: usingExams,
    },
    {
      name: 'Sair',
      url: createUrl('logout'),
      onClick: () => {
        eventTracker({
          category: 'Usuário',
          action: 'Logout',
          label: 'Menu hamburguer',
        });
      },
    },
  ];

  return isOpened ? (
    <Wrapper>
      <MenuWrapper>
        <CloseMenu schoolAlias={schoolAlias} onClick={menuClose}>
          <CloseIconWrapper>
            <CloseIcon icon="close" />
          </CloseIconWrapper>

          <CloseLabel>Fechar menu</CloseLabel>
        </CloseMenu>

        <MenuScrolling>
          <DashboardSelector dashboards={dashboards} onClick={menuClose} />

          <Flex flexDirection="column" py={2}>
            {menu
              .filter((item) => typeof item.show === 'undefined' || item.show === true)
              .map((item, index) => {
                return (
                  <MenuItem key={`menu2-${index.toString()}`} type="internalNav" to={item.url} activeStyle={{ fontWeight: 700 }} onClick={menuClose}>
                    <span>{item.name}</span>

                    <MenuIcon icon="arrowRight" />
                  </MenuItem>
                );
              })}
          </Flex>
        </MenuScrolling>
      </MenuWrapper>

      <Opacity onClick={menuClose} />
    </Wrapper>
  ) : (
    <></>
  );
};

export default Menu;
